import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { StatusPill } from "../components/StatusPill";
import { date } from "../lib/format";

interface AdminServer { id: string; name: string; status: string; provider: string | null; providerServerId: string | null; lastError: string | null; createdAt: string; }
interface AdminJob { id: string; type: string; status: string; attempts: number; maxAttempts: number; lastError: string | null; }

export function Admin() {
  const [tab, setTab] = useState<"servers" | "jobs" | "subs" | "users">("servers");
  const [servers, setServers] = useState<AdminServer[]>([]);
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [subs, setSubs] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, j, su, u] = await Promise.all([
        api.get<{ servers: AdminServer[] }>("/admin/servers"),
        api.get<{ jobs: AdminJob[] }>("/admin/jobs"),
        api.get<{ subscriptions: any[] }>("/admin/subscriptions"),
        api.get<{ users: any[] }>("/admin/users"),
      ]);
      setServers(s.servers); setJobs(j.jobs); setSubs(su.subscriptions); setUsers(u.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load admin data");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function op(fn: () => Promise<unknown>, key: string) {
    setBusy(key);
    try { await fn(); await load(); } catch (e) { setError(e instanceof Error ? e.message : "Failed"); } finally { setBusy(""); }
  }

  return (
    <div className="page">
      <h1>Admin</h1>
      <p className="muted">Most provisioning issues are resolved from here.</p>
      {error && <div className="error-box mt">{error}</div>}

      <div className="row mt">
        {(["servers", "jobs", "subs", "users"] as const).map((t) => (
          <button key={t} className={`btn btn-sm ${tab === t ? "btn-primary" : "btn-ghost"}`} onClick={() => setTab(t)} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "servers" && (
        <table className="table mt" data-testid="admin-servers">
          <thead><tr><th>Name</th><th>Status</th><th>Provider</th><th>VM ID</th><th>Error</th><th>Actions</th></tr></thead>
          <tbody>
            {servers.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td><StatusPill status={s.status} /></td>
                <td className="muted">{s.provider ?? "—"}</td>
                <td className="muted">{s.providerServerId ?? "—"}</td>
                <td className="muted">{s.lastError ?? "—"}</td>
                <td>
                  <div className="row">
                    <button className="btn btn-sm" disabled={!!busy} onClick={() => op(() => api.post(`/admin/servers/${s.id}/suspend`), "su")}>Suspend</button>
                    <button className="btn btn-sm btn-danger" disabled={!!busy} onClick={() => op(() => api.post(`/admin/servers/${s.id}/delete`), "del")}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "jobs" && (
        <table className="table mt" data-testid="admin-jobs">
          <thead><tr><th>Type</th><th>Status</th><th>Attempts</th><th>Error</th><th></th></tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td>{j.type}</td><td>{j.status}</td><td>{j.attempts}/{j.maxAttempts}</td><td className="muted">{j.lastError ?? "—"}</td>
                <td>{(j.status === "failed") && <button className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => op(() => api.post(`/admin/jobs/${j.id}/retry`), "retry")}>Retry</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "subs" && (
        <table className="table mt" data-testid="admin-subs">
          <thead><tr><th>Stripe Sub</th><th>Customer</th><th>Status</th><th>Cancel at period end</th><th>Period end</th></tr></thead>
          <tbody>
            {subs.map((s) => (
              <tr key={s.id}><td>{s.stripeSubscriptionId}</td><td>{s.stripeCustomerId}</td><td>{s.status}</td><td>{String(s.cancelAtPeriodEnd)}</td><td>{date(s.currentPeriodEnd)}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "users" && (
        <table className="table mt" data-testid="admin-users">
          <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Joined</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}><td>{u.email}</td><td>{u.name ?? "—"}</td><td>{u.role}</td><td>{date(u.createdAt)}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
